import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { toastUtils } from "../../Utils/helper";
import Button from "../../components/common/Button/Button";
import { CheckboxCommon } from "../../components/common/CheckBox/CheckboxCommon";
import CustomInputProps from "../../components/CustomInputProps/CustomInputProps";
import images from "../../images";
import { useLoginMutation } from "../../services/apiSlice";

// Constants
const DEFAULT_COUNTRY_CODE = "+65";
const FROM_QRCODE = "qrcode";
const TOAST_MESSAGES = {
  OTP_SUCCESS: "toast.success.otpCodeWillExprireIn5Minutes",
  LOGIN_FAILED: "toast.error.loginFailed",
  INVALID_PHONE: "signIn.invalidPhoneNumber",
};

// Utility Functions
const parseQueryParams = (search) => {
  const params = new URLSearchParams(search);
  return {
    questId: params.get("quest_id"),
    locationId: params.get("location_id"),
    isFromQRCode: params.get("from") === FROM_QRCODE,
    code: params.get("code"),
  };
};

const parsePhoneNumber = (phone) => {
  try {
    const phoneFirst8 = phone.slice(0, 8);

    const phoneWithCountryCode = phoneFirst8.startsWith("+")
      ? phoneFirst8
      : `${DEFAULT_COUNTRY_CODE}${phoneFirst8}`;

    const phoneNumber = parsePhoneNumberFromString(phoneWithCountryCode);
    if (phoneNumber && phoneNumber.isValid()) {
      const country = phoneNumber.country;
      const countryCode = country ? `+${getCountryCallingCode(country)}` : "";
      // Trả về 8 số đầu tiên (bỏ country code)
      const phoneWithoutCountryCode = phoneFirst8;
      return { countryCode, phoneWithoutCountryCode, isValid: true };
    }
    return { countryCode: "", phoneWithoutCountryCode: "", isValid: false };
  } catch {
    return { countryCode: "", phoneWithoutCountryCode: "", isValid: false };
  }
};

const handleApiError = (error, t, isFromQRCode, questId, locationId, code) => {
  const status = error?.status || error?.response?.status;

  if (status === 400) {
    return t("toast.error.verifyingOtp");
  }

  if (status === 422) {
    if (
      error?.data?.detail?.[0]?.msg ===
      "Your number is not registered. Please create account"
    ) {
      return (
        <span>
          {t("signIn.yourNumberIsNotRegistered")}{" "}
          <Link
            to={`/register?${
              isFromQRCode || questId || locationId || code
                ? `from=${FROM_QRCODE}&quest_id=${questId}&location_id=${locationId}${code ? `&code=${code}` : ""}`
                : ""
            }`}
            className="text-[#EF4056] font-bold transition-colors"
          >
            {t("signUp.signUp")}
          </Link>
        </span>
      );
    } else {
      const fallbackMsg =
        error?.data?.detail?.[0]?.msg ||
        error?.response?.data?.detail?.[0]?.msg ||
        t(TOAST_MESSAGES.LOGIN_FAILED);

      return fallbackMsg;
    }
  }
};

const SignInForm = ({
  signInData,
  setSignInData,
  rememberMe,
  setRememberMe,
  onSubmit,
  questId,
  locationId,
  isFromQRCode,
  error,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  const handlePhoneChange = useCallback(
    (value) => {
      const rawPhone =
        typeof value === "string" ? value.replace(/[^0-9]/g, "") : "";

      const phoneFirst8Digits = rawPhone.slice(0, 8);

      const formattedPhone =
        phoneFirst8Digits.length > 4
          ? `${phoneFirst8Digits.slice(0, 4)}-${phoneFirst8Digits.slice(4, 8)}`
          : phoneFirst8Digits;

      const parsed = parsePhoneNumber(phoneFirst8Digits);
      setSignInData({
        phoneNumber: formattedPhone,
        rawPhoneNumber: phoneFirst8Digits,
        countryCode: parsed.countryCode,
        phoneWithoutCountryCode: parsed.phoneWithoutCountryCode,
        isPhoneValid: parsed.isValid,
      });
    },
    [setSignInData],
  );

  return (
    <div className="flex flex-col h-full justify-between">
      <div className="flex flex-col items-center gap-6">
        <label htmlFor="phone-input" className="sr-only">
          {t("signIn.phoneNumber")}
        </label>
        <div className="w-full flex flex-col gap-6">
          <CustomInputProps
            value={signInData.phoneNumber}
            onChange={handlePhoneChange}
            placeholder={t("signUp.whatsAppNumber")}
            countryCode={signInData.countryCode || DEFAULT_COUNTRY_CODE}
            iconDropdown
          />
          {error.phone && (
            <p className="text-destructive text-sm">{error.phone}</p>
          )}
        </div>

        <label className="flex items-center gap-2 w-full cursor-pointer">
          <div className="size-[30px] flex items-center justify-center">
            <CheckboxCommon
              className="rounded-[2px] size-5 border-2 border-mid-gray data-[state=checked]:bg-white data-[state=checked]:text-mid-gray"
              checked={rememberMe}
              onCheckedChange={setRememberMe}
              id="remember-me"
              aria-label={t("signIn.rememberMe")}
            />
          </div>
          <span className="text-mid-gray text-xs leading-[14px] font-medium">
            {t("signIn.rememberMe")}
          </span>
        </label>

        <div className="flex items-center w-full font-medium gap-1 text-[14px]">
          <span className="text-mid-gray">{t("signIn.noAccount")}</span>{" "}
          <button
            onClick={() =>
              navigate(
                `/register?${
                  isFromQRCode
                    ? `from=${FROM_QRCODE}&quest_id=${questId}&location_id=${locationId}${code ? `&code=${code}` : ""}`
                    : ""
                }`,
              )
            }
            className="text-primary font-bold"
            aria-label={t("signUp.signUp")}
          >
            {t("signUp.signUp")}
          </button>
        </div>
      </div>
      <Button
        className="h-[50px] text-[20px] font-medium text-white bg-primary rounded-md w-full mt-0"
        text={t("signUp.getWhatsAppOTP")}
        iconSub={images.WhatsappIconWhite}
        onClick={onSubmit}
        disabled={!signInData.isPhoneValid}
        aria-label={t("signUp.getWhatsAppOTP")}
      />
    </div>
  );
};

// Main Component
const SignInPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [login] = useLoginMutation();
  const { state } = useLocation();

  const [error, setError] = useState({
    phone: "",
    isPhoneValid: false,
  });
  const [signInData, setSignInData] = useState({
    phoneNumber: "",
    rawPhoneNumber: "",
    countryCode: "+65",
    phoneWithoutCountryCode: "",
    isPhoneValid: false,
  });
  const [rememberMe, setRememberMe] = useState(false);
  const { questId, locationId, isFromQRCode, code } = parseQueryParams(
    location.search,
  );

  const handleSubmit = useCallback(() => {
    setError({
      phone: "",
      privacyPolicy: "",
    });

    const body = {
      phone: signInData.rawPhoneNumber,
      country_code: signInData.countryCode,
    };

    // Hàm xây dựng URL cho verify-otp
    const buildVerifyOtpUrl = () => {
      const params = new URLSearchParams({
        phone: signInData.phoneWithoutCountryCode || "",
        country_code: signInData.countryCode || "",
        quest_id: questId || "",
        location_id: locationId || "",
        remember_me: rememberMe || "false",
      });

      if (code) {
        params.append("code", code);
      }
      // --------------------

      if (state?.from === "notifications") {
        params.append("from", "notifications");
      }

      return `/verify-otp?${params.toString()}`;
    };

    login(body)
      .unwrap()
      .then((response) => {
        if (response.status === 200) {
          toastUtils.success(t(TOAST_MESSAGES.OTP_SUCCESS));
          navigate(buildVerifyOtpUrl());
        }
      })
      .catch((error) => {
        setError({
          phone: handleApiError(
            error,
            t,
            isFromQRCode,
            questId,
            locationId,
            code,
          ),
        });
      });
  }, [
    signInData,
    rememberMe,
    questId,
    locationId,
    login,
    navigate,
    t,
    state,
    code,
    isFromQRCode,
  ]);

  return (
    <div className="section-h-full flex flex-col px-10 max-xs:py-9 py-[72px] max-xs:gap-[30px] gap-[60px] 2xs:max-w-[430px]">
      <Helmet>
        <meta name="theme-color" content="#ffffff" />
      </Helmet>
      <div className="flex items-center">
        <img src={images.LogoLogin} className="w-auto h-[60px]" alt="logo" />
      </div>
      <h1 className="text-4xl leading-none font-bold text-primary tracking-[-0.72px]">
        {t("signIn.title")}
      </h1>
      <div className="h-full">
        <SignInForm
          signInData={signInData}
          setSignInData={setSignInData}
          rememberMe={rememberMe}
          setRememberMe={setRememberMe}
          onSubmit={handleSubmit}
          questId={questId}
          locationId={locationId}
          isFromQRCode={isFromQRCode}
          error={error}
        />
      </div>
    </div>
  );
};

export default SignInPage;
